// AdminPage – admin-only dashboard: firmware upload, events, operator assignment, session info.

import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Shield,
  User,
  CircleCheck,
  CircleX,
  CircleHelp,
  ExternalLink,
  FileText,
} from 'lucide-react';
import {
  parseFirmwareListPayload,
  type FirmwareArtifactRow,
} from '../helpers/firmwareArtifacts';
import { useAuth } from '../context/AuthContext';
import FirmwareUploadPanel from '../components/FirmwareUploadView/FirmwareUploadView';
import ConfirmDialog from '../components/ConfirmDialog';
import RefreshButton from '../components/shared/RefreshButton';
import InlineSpinner from '../components/shared/InlineSpinner';
import {
  PAGE_TAB_HEADER,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '../components/shared/ui';
import { buildGlobalUserIdToDisplayName } from '../components/EventsManagementView/utils';
import { formatUserIdentityDisplay } from '../helpers/helpers';
import {
  showPrefixedToastError,
  showToastError,
  showToastErrorMessage,
} from '../helpers/toastError';

// ---- Types ----

type AlarmState = 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';

type AlarmStatus = {
  state: AlarmState;
  reason?: string | null;
  updatedAt?: number | null;
  alarmName?: string | null;
  consoleUrl?: string | null;
};

/** Compact "how long ago" label for an alarm's last state change (e.g. "2m ago", "3h ago"). */
function formatRelativeTime(ms: number): string {
  const diffMs = Date.now() - ms;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

type SystemHealthRow = {
  resource: string;
  metrics: Record<string, AlarmStatus>;
};

type SystemHealthCategories = Record<string, SystemHealthRow[]>;

/** Display order + label for known categories; anything else falls back to its raw key, sorted after these. */
const HEALTH_CATEGORY_LABELS: Record<string, string> = {
  lambda: 'Lambda',
  iot_rule: 'IoT rules',
  dynamodb: 'DynamoDB',
  apigateway: 'API Gateway',
};
const HEALTH_CATEGORY_ORDER = ['lambda', 'iot_rule', 'dynamodb', 'apigateway'];

/** Display order + label for known metric keys within a category's columns. */
const HEALTH_METRIC_LABELS: Record<string, string> = {
  errors: 'Errors',
  throttles: 'Throttles',
  duration: 'Duration',
  parse_error: 'Parse errors',
  action_failure: 'Action failures',
  read_throttle: 'Read throttle',
  write_throttle: 'Write throttle',
  error_5xx: '5xx rate',
  error_4xx: '4xx rate',
  latency: 'Latency',
};

type ExpandableSectionProps = {
  title: string;
  description?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  right?: React.ReactNode;
};

// ---- Helpers ----

/** Collapsible card: title + optional description in header, Show/Hide toggles body. */
function ExpandableSection(props: ExpandableSectionProps): JSX.Element {
  const {
    title,
    description,
    children,
    defaultExpanded = false,
    right,
  } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden dark:border-slate-700 dark:bg-slate-900">
      {/* Section header: title, description, optional right slot, expand/collapse button */}
      <header className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
            {title}
          </div>
          {description ? (
            <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {description}
            </div>
          ) : null}
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {right}
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
        </div>
      </header>

      {expanded ? <div className="p-4">{children}</div> : null}
    </section>
  );
}

/** Colored pill for a single CloudWatch alarm's state (OK / ALARM / INSUFFICIENT_DATA). The "last
 * changed" time and the CloudWatch console link are tucked away (tooltip / hover-reveal) rather than
 * always-on, so a table full of these stays scannable instead of every cell taking two rows. */
function AlarmStatePill({ status }: { status?: AlarmStatus }): JSX.Element {
  const updatedLabel =
    typeof status?.updatedAt === 'number'
      ? formatRelativeTime(status.updatedAt)
      : null;
  const tooltip =
    [status?.reason, updatedLabel ? `Changed ${updatedLabel}` : null]
      .filter(Boolean)
      .join(' — ') || undefined;

  return (
    <div className="group inline-flex items-center gap-1">
      <AlarmStateBadge status={status} tooltip={tooltip} />
      {status?.consoleUrl ? (
        <button
          type="button"
          title="Open in CloudWatch console"
          onClick={() =>
            window.open(
              status.consoleUrl as string,
              '_blank',
              'noopener,noreferrer',
            )
          }
          className="text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 hover:text-gray-700 dark:text-slate-600 dark:hover:text-slate-200"
        >
          <ExternalLink className="h-3 w-3" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

function AlarmStateBadge({
  status,
  tooltip,
}: {
  status?: AlarmStatus;
  tooltip?: string;
}): JSX.Element {
  const state = status?.state ?? 'INSUFFICIENT_DATA';

  if (state === 'OK') {
    return (
      <span
        title={tooltip}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-semibold dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200"
      >
        <CircleCheck className="h-3.5 w-3.5" aria-hidden />
        OK
      </span>
    );
  }

  if (state === 'ALARM') {
    return (
      <span
        title={tooltip}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-red-300 bg-red-50 text-red-800 text-[11px] font-semibold dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200"
      >
        <CircleX className="h-3.5 w-3.5" aria-hidden />
        ALARM
      </span>
    );
  }

  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-gray-100 text-gray-400 text-[11px] dark:border-slate-700 dark:text-slate-500"
    >
      <CircleHelp className="h-3 w-3" aria-hidden />
      No data
    </span>
  );
}

/** Recent CloudWatch log tail for a single Lambda, opened from the System health table. */
function LambdaLogsDialog({
  functionName,
  onClose,
}: {
  functionName: string;
  onClose: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState<
    Array<{ timestampMs?: number; message?: string }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setBusy(true);
      setError('');
      try {
        const endTimeMs = Date.now();
        const startTimeMs = endTimeMs - 15 * 60 * 1000;
        const res = await window.eventsAPI.getLambdaLogs({
          functionName,
          startTimeMs,
          endTimeMs,
          limit: 200,
        });
        if (cancelled) return;
        if (!res?.ok) throw new Error(res?.error || 'Failed to load logs');
        setEvents(Array.isArray(res.data?.events) ? res.data.events : []);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load logs');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [functionName]);

  return (
    <Dialog open onClose={onClose} closeOnBackdrop panelClassName="max-w-3xl">
      <DialogHeader>
        <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 font-mono break-all">
          {functionName}
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-400">
          Last 15 minutes
        </div>
      </DialogHeader>
      <DialogBody className="max-h-[60vh] overflow-auto">
        {busy ? (
          <div className="flex items-center justify-center py-8">
            <InlineSpinner size="sm" variant="accent" />
          </div>
        ) : error ? (
          <div className="text-xs text-red-700 dark:text-red-300">{error}</div>
        ) : events.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-slate-400">
            No log events in this window.
          </div>
        ) : (
          <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-gray-800 dark:text-slate-200">
            {events
              .map(
                (e) =>
                  `${e.timestampMs ? new Date(e.timestampMs).toISOString() : ''}  ${e.message ?? ''}`,
              )
              .join('\n')}
          </pre>
        )}
      </DialogBody>
      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-xs rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          Close
        </button>
      </DialogFooter>
    </Dialog>
  );
}

// ---- Component ----

export default function AdminPage(): JSX.Element {
  const { role, isAdmin, isOperator } = useAuth();

  // Config from main (via preload).
  const sessionItems: { label: string; value: string | undefined }[] =
    useMemo(() => {
      const e = window.fmPublicEnv;
      return [
        { label: 'PORT', value: e?.PORT },
        { label: 'Access level', value: 'Admin' },
        { label: 'AWS_REGION', value: e?.AWS_REGION },
        {
          label: 'AWS_COGNITO_IDENTITY_POOL_ID',
          value: e?.AWS_COGNITO_IDENTITY_POOL_ID,
        },
        { label: 'AWS_COGNITO_CLIENT_ID', value: e?.AWS_COGNITO_CLIENT_ID },
        { label: 'AWS_COGNITO_DOMAIN', value: e?.AWS_COGNITO_DOMAIN },
        {
          label: 'AWS_COGNITO_REDIRECT_URI',
          value: e?.AWS_COGNITO_REDIRECT_URI,
        },
        { label: 'FLEET_STATUS_TABLE', value: e?.FLEET_STATUS_TABLE },
        { label: 'FLEET_COMMANDS_TABLE', value: e?.FLEET_COMMANDS_TABLE },
        { label: 'FLEET_REQUESTS_TABLE', value: e?.FLEET_REQUESTS_TABLE },
        { label: 'AWS_FLEET_WS_URL', value: e?.AWS_FLEET_WS_URL },
        {
          label: 'AWS_FLEET_COMMAND_API_URL',
          value: e?.AWS_FLEET_COMMAND_API_URL,
        },
        { label: 'AWS_EVENTS_API_URL', value: e?.AWS_EVENTS_API_URL },
        { label: 'DEVICE_TLS_BUCKET', value: e?.DEVICE_TLS_BUCKET },
        { label: 'DEVICE_FW_BUCKET', value: e?.DEVICE_FW_BUCKET },
        { label: 'DEVICE_LIFECYCLE_TABLE', value: e?.DEVICE_LIFECYCLE_TABLE },
        {
          label: 'RACE_DEVICES_HISTORY_TABLE',
          value: e?.RACE_DEVICES_HISTORY_TABLE,
        },
        { label: 'EVENT_RACES_TABLE', value: e?.EVENT_RACES_TABLE },
      ];
    }, []);

  const [users, setUsers] = useState<
    Array<{
      userId?: string;
      username?: string;
      email?: string;
      sub?: string;
      group?: string;
    }>
  >([]);
  const [usersBusy, setUsersBusy] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    group: 'operator',
    temporaryPassword: '',
  });

  const canCreateUser = useMemo(() => {
    const username = createForm.username.trim();
    const email = createForm.email.trim();
    const group = createForm.group.trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    return username.length > 0 && emailOk && group.length > 0;
  }, [createForm]);

  const userIdToDisplayName = useMemo(
    () => buildGlobalUserIdToDisplayName(users as unknown[]),
    [users],
  );

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    setUsersBusy(true);
    try {
      const res = await window.eventsAPI.listUsers();
      if (!res?.ok) throw new Error(res?.error || 'Failed to load users');
      const data = res.data as { users?: unknown[] } | unknown[] | undefined;
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.users)
          ? data.users
          : [];
      setUsers(
        rows.map((x: any) => ({
          userId: String(x?.userId ?? '').trim() || undefined,
          sub: String(x?.sub ?? '').trim() || undefined,
          username: String(x?.username ?? '').trim() || undefined,
          email: String(x?.email ?? '').trim() || undefined,
          group: String(x?.group ?? '').trim() || undefined,
        })),
      );
    } catch (err: any) {
      showPrefixedToastError('Failed to load users', err);
      setUsers([]);
    } finally {
      setUsersBusy(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const [systemHealth, setSystemHealth] = useState<SystemHealthCategories>({});
  const [lambdaLogsFunction, setLambdaLogsFunction] = useState<string | null>(
    null,
  );
  const [systemHealthBusy, setSystemHealthBusy] = useState(false);

  const loadSystemHealth = useCallback(async () => {
    if (!isAdmin) return;
    setSystemHealthBusy(true);
    try {
      const res = await window.eventsAPI.getSystemHealth();
      if (!res?.ok)
        throw new Error(res?.error || 'Failed to load system health');
      const categories = res.data?.categories;
      setSystemHealth(
        categories && typeof categories === 'object' ? categories : {},
      );
    } catch (err: any) {
      showPrefixedToastError('Failed to load system health', err);
      setSystemHealth({});
    } finally {
      setSystemHealthBusy(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadSystemHealth();
  }, [loadSystemHealth]);

  const systemHealthCategoryKeys = useMemo(() => {
    const keys = Object.keys(systemHealth);
    return keys.sort((a, b) => {
      const ai = HEALTH_CATEGORY_ORDER.indexOf(a);
      const bi = HEALTH_CATEGORY_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [systemHealth]);

  const [showOnlyProblems, setShowOnlyProblems] = useState(false);

  /** Alarm-state counts across every metric of every resource, and per-category alarm counts —
   * drives the summary strip and the category header badges. */
  const systemHealthSummary = useMemo(() => {
    let ok = 0;
    let alarm = 0;
    let noData = 0;
    const alarmsByCategory: Record<string, number> = {};

    for (const [category, rows] of Object.entries(systemHealth)) {
      let categoryAlarms = 0;
      for (const row of rows) {
        for (const status of Object.values(row.metrics)) {
          if (status.state === 'OK') ok += 1;
          else if (status.state === 'ALARM') {
            alarm += 1;
            categoryAlarms += 1;
          } else noData += 1;
        }
      }
      alarmsByCategory[category] = categoryAlarms;
    }

    return { ok, alarm, noData, alarmsByCategory };
  }, [systemHealth]);

  const [firmwareRole, setFirmwareRole] = useState<'rear' | 'front'>('rear');
  const [firmwareArtifacts, setFirmwareArtifacts] = useState<
    FirmwareArtifactRow[]
  >([]);
  const [firmwareVersionsBusy, setFirmwareVersionsBusy] = useState(false);

  const loadFirmwareVersions = useCallback(
    async (role: 'rear' | 'front'): Promise<void> => {
      if (!isAdmin) return;
      setFirmwareVersionsBusy(true);
      try {
        const res = await window.eventsAPI.getFirmwareVersions(role);
        if (!res?.ok)
          throw new Error(res?.error || 'Failed to load firmware versions');
        const { artifacts } = parseFirmwareListPayload(res, {
          defaultRole: role,
        });
        setFirmwareArtifacts(artifacts);
        if (artifacts.length === 0) {
          window.dispatchEvent(
            new CustomEvent('toast-error', {
              detail: 'No firmware versions returned',
            }),
          );
        }
      } catch (err: any) {
        showToastError(err);
      } finally {
        setFirmwareVersionsBusy(false);
      }
    },
    [isAdmin],
  );

  useEffect(() => {
    void loadFirmwareVersions(firmwareRole);
  }, [loadFirmwareVersions, firmwareRole]);

  const createUser = useCallback(async () => {
    if (!canCreateUser) return;
    setCreateBusy(true);
    try {
      const res = await window.eventsAPI.createUser({
        username: createForm.username.trim(),
        email: createForm.email.trim(),
        group: createForm.group.trim() || undefined,
        temporaryPassword: createForm.temporaryPassword.trim() || undefined,
      });
      if (!res?.ok) throw new Error(res?.error || 'Failed to create user');
      window.dispatchEvent(
        new CustomEvent('toast-info', {
          detail: `Created user ${createForm.username.trim()}`,
        }),
      );
      setCreateForm({
        username: '',
        email: '',
        group: 'operator',
        temporaryPassword: '',
      });
      await loadUsers();
    } catch (err: any) {
      showPrefixedToastError('Create user failed', err);
    } finally {
      setCreateBusy(false);
    }
  }, [canCreateUser, createForm, loadUsers]);

  type AdminConfirmState =
    | { kind: 'deleteUser'; userId: string }
    | { kind: 'deleteFirmware'; role: 'rear' | 'front'; fileName: string }
    | { kind: 'removeOperator'; eventId: string; userId: string }
    | null;
  const [adminConfirm, setAdminConfirm] = useState<AdminConfirmState>(null);

  const runAdminAction = useCallback(
    async (label: string, action: () => Promise<any>) => {
      setActionBusy(true);
      try {
        const res = await action();
        if (!res?.ok) throw new Error(res?.error || `Failed: ${label}`);
        window.dispatchEvent(
          new CustomEvent('toast-info', { detail: `${label} complete` }),
        );
        await loadUsers();
      } catch (err: any) {
        showPrefixedToastError(`${label} failed`, err);
      } finally {
        setActionBusy(false);
      }
    },
    [loadUsers],
  );

  const deprecateFirmwareVersion = useCallback(
    (fileName: string): void => {
      const f = String(fileName || '').trim();
      if (!f) return;
      setAdminConfirm({
        kind: 'deleteFirmware',
        role: firmwareRole,
        fileName: f,
      });
    },
    [firmwareRole],
  );

  const performDeprecateFirmware = useCallback(
    async (role: 'rear' | 'front', fileName: string): Promise<void> => {
      const f = String(fileName || '').trim();
      if (!f) return;

      setActionBusy(true);
      try {
        const res = await window.eventsAPI.deprecateFirmware(role, f);
        if (!res?.ok)
          throw new Error(res?.error || 'Failed to delete firmware');
        window.dispatchEvent(
          new CustomEvent('toast-info', { detail: `Firmware deleted: ${f}` }),
        );
        await loadFirmwareVersions(role);
      } catch (err: any) {
        showToastError(err);
      } finally {
        setActionBusy(false);
      }
    },
    [loadFirmwareVersions],
  );

  const performAdminConfirm = useCallback(async (): Promise<void> => {
    if (!adminConfirm) return;
    const current = adminConfirm;

    try {
      if (current.kind === 'deleteUser') {
        await runAdminAction('Delete user', () =>
          window.eventsAPI.disableUser(current.userId),
        );
        return;
      }

      if (current.kind === 'removeOperator') {
        await runAdminAction('Remove operator', () =>
          window.eventsAPI.removeEventOperators(current.eventId, [
            current.userId,
          ]),
        );
        return;
      }

      if (current.kind === 'deleteFirmware') {
        await performDeprecateFirmware(current.role, current.fileName);
      }
    } finally {
      setAdminConfirm(null);
    }
  }, [adminConfirm, performDeprecateFirmware, runAdminAction]);

  // Non-admin sees access denied card only.
  if (!isAdmin) {
    return (
      <div className="h-full min-h-0 flex items-center justify-center p-6 bg-gray-50 dark:bg-slate-800">
        <div className="max-w-md w-full rounded-xl border border-red-200 bg-white shadow-sm overflow-hidden dark:border-red-800/60 dark:bg-slate-900">
          <div className="px-4 py-3 border-b border-red-100 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30 dark:border-red-900/60 dark:bg-red-950/30 dark:border-red-900/60 dark:bg-red-950/30">
            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              Access denied
            </div>
            <div className="text-xs text-red-700 mt-0.5">
              Admin permissions are required.
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-700 dark:text-slate-300">
              Your role <span className="font-semibold">{role}</span> does not
              have permission to view this page.
            </p>
            {isOperator ? (
              <p className="mt-2 text-sm text-gray-700 dark:text-slate-300">
                You are an operator, but admin rights are required here.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-slate-100 dark:bg-slate-800">
      {/* Page header – title and description (stage / version live in AppHeader). */}
      <div className={PAGE_TAB_HEADER}>
        <div className="text-lg font-semibold">Admin dashboard</div>
        <div className="text-sm text-gray-500 dark:text-slate-400">
          Firmware, events, and operator management.
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 overflow-auto">
        <div className="grid gap-4">
          {/* Single admin column */}
          <div className="flex flex-col gap-4 min-h-0">
            <ExpandableSection
              title="Session info"
              description="Environment and session metadata"
            >
              <div className="max-h-80 overflow-auto pr-1">
                <dl className="space-y-3">
                  {sessionItems.map((item) => (
                    <div key={item.label} className="flex flex-col gap-1">
                      <dt className="text-xs font-semibold text-gray-700 dark:text-slate-300">
                        {item.label}
                      </dt>
                      <dd className="font-mono text-[11px] text-gray-600 dark:text-slate-400 break-all">
                        {item.value || '—'}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </ExpandableSection>

            <ExpandableSection
              title="Firmware upload"
              description="Upload firmware binaries so they are available for device updates."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="px-4 py-3 text-[11px] space-y-3 rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                    Add firmware
                  </div>
                  <FirmwareUploadPanel
                    onUploaded={() => loadFirmwareVersions(firmwareRole)}
                  />
                </div>

                <div className="rounded-lg border border-gray-200 p-3 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                      Firmware versions
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        className="px-2 py-1 rounded border border-gray-300 bg-white text-[11px] text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        value={firmwareRole}
                        onChange={(e) =>
                          setFirmwareRole(e.target.value as 'rear' | 'front')
                        }
                        disabled={firmwareVersionsBusy || actionBusy}
                      >
                        <option value="rear">rear</option>
                        <option value="front">front</option>
                      </select>
                      <RefreshButton
                        busy={firmwareVersionsBusy}
                        onClick={() => void loadFirmwareVersions(firmwareRole)}
                        disabled={actionBusy}
                        className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        busyText="Loading..."
                        busyClassName="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400"
                      />
                    </div>
                  </div>

                  <div className="relative max-h-72 overflow-auto border border-gray-200 rounded dark:border-slate-700">
                    {firmwareArtifacts.length === 0 ? (
                      <div className="p-3 text-xs text-gray-500 dark:text-slate-400">
                        {firmwareVersionsBusy
                          ? 'Loading firmware versions…'
                          : 'No firmware versions found.'}
                      </div>
                    ) : (
                      <table className="min-w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800">
                          <tr>
                            <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                              Version
                            </th>
                            <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                              File
                            </th>
                            <th className="text-right p-2 border-b border-gray-200 dark:border-slate-700">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {firmwareArtifacts.map((a) => (
                            <tr
                              key={a.key}
                              className="odd:bg-white even:bg-gray-50/40 dark:even:bg-slate-800/40 dark:odd:bg-slate-900 dark:even:bg-slate-800/40"
                            >
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700 font-mono text-[11px] text-gray-700 dark:text-slate-300 break-all">
                                {a.version}
                              </td>
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700 font-mono text-[11px] text-gray-600 dark:text-slate-400 break-all">
                                {a.fileName}
                              </td>
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700 text-right">
                                <button
                                  type="button"
                                  className="px-2 py-1 text-[11px] rounded border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 disabled:opacity-50 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/60 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/60 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/60"
                                  disabled={actionBusy}
                                  onClick={() =>
                                    void deprecateFirmwareVersion(a.fileName)
                                  }
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {firmwareVersionsBusy ? (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur pointer-events-none">
                        <InlineSpinner size="sm" variant="accent" />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </ExpandableSection>

            <ExpandableSection
              title="User access"
              description="Create Cognito users and assign access group."
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="px-4 py-3 text-[11px] space-y-3 rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                    Create user
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <input
                      className="px-3 py-2 text-[11px] rounded-md border border-gray-300 bg-white text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      placeholder="Username *"
                      value={createForm.username}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          username: e.target.value,
                        }))
                      }
                    />
                    <input
                      className="px-3 py-2 text-[11px] rounded-md border border-gray-300 bg-white text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                      placeholder="Email *"
                      value={createForm.email}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, email: e.target.value }))
                      }
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="px-3 py-2 text-[11px] rounded-md border border-gray-300 bg-white text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        value={createForm.group}
                        onChange={(e) =>
                          setCreateForm((p) => ({
                            ...p,
                            group: e.target.value,
                          }))
                        }
                      >
                        <option value="operator">operator</option>
                        <option value="admin">admin</option>
                      </select>
                      <input
                        className="px-3 py-2 text-[11px] rounded-md border border-gray-300 bg-white text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        placeholder="Temp password (optional)"
                        value={createForm.temporaryPassword}
                        onChange={(e) =>
                          setCreateForm((p) => ({
                            ...p,
                            temporaryPassword: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                        onClick={() => void createUser()}
                        disabled={!canCreateUser || createBusy}
                      >
                        {createBusy ? (
                          <>
                            <InlineSpinner size="xs" variant="current" />
                            Creating…
                          </>
                        ) : (
                          'Create User'
                        )}
                      </button>
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        * required
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 p-3 bg-white dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                      Users
                    </div>
                    <RefreshButton
                      busy={usersBusy}
                      onClick={() => void loadUsers()}
                      className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      busyText="Refreshing..."
                      busyClassName="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400"
                    />
                  </div>
                  <div className="relative max-h-72 overflow-auto border border-gray-200 rounded dark:border-slate-700">
                    {users.length === 0 ? (
                      <div className="p-3 text-xs text-gray-500 dark:text-slate-400">
                        {usersBusy ? 'Loading users…' : 'No users found.'}
                      </div>
                    ) : (
                      <table className="min-w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800">
                          <tr>
                            <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                              Username
                            </th>
                            <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                              Email
                            </th>
                            <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                              User ID
                            </th>
                            <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                              Role
                            </th>
                            <th className="text-right p-2 border-b border-gray-200 dark:border-slate-700">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u, i) => (
                            <tr
                              key={`${u.userId || u.sub || u.username || 'user'}-${i}`}
                              className="odd:bg-white even:bg-gray-50/40 dark:even:bg-slate-800/40 dark:odd:bg-slate-900 dark:even:bg-slate-800/40"
                            >
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700">
                                {u.username || '—'}
                              </td>
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700">
                                {u.email || '—'}
                              </td>
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700 text-[11px] text-gray-700 dark:text-slate-300">
                                {formatUserIdentityDisplay(
                                  u.userId || u.sub || '',
                                  userIdToDisplayName,
                                )}
                              </td>
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700">
                                {String(u.group || '').toLowerCase() ===
                                'admin' ? (
                                  <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-800 text-[11px] font-semibold dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200">
                                    <Shield
                                      className="h-3.5 w-3.5"
                                      aria-hidden
                                    />
                                    Admin
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-800 text-[11px] font-semibold dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-200">
                                    <User className="h-3.5 w-3.5" aria-hidden />
                                    Operator
                                  </span>
                                )}
                              </td>
                              <td className="p-2 border-b border-gray-100 dark:border-slate-700 text-right">
                                <button
                                  type="button"
                                  className="px-2 py-1 text-[11px] rounded border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 disabled:opacity-50 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/60 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/60 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/60"
                                  disabled={actionBusy || !(u.userId || u.sub)}
                                  onClick={() => {
                                    const id = String(
                                      u.userId || u.sub || '',
                                    ).trim();
                                    if (!id) return;
                                    setAdminConfirm({
                                      kind: 'deleteUser',
                                      userId: id,
                                    });
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {usersBusy ? (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur pointer-events-none">
                        <InlineSpinner size="sm" variant="accent" />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </ExpandableSection>

            <ExpandableSection
              title="System health"
              description="CloudWatch alarm state across Lambda, IoT rules, DynamoDB, and API Gateway."
              right={
                <RefreshButton
                  busy={systemHealthBusy}
                  onClick={() => void loadSystemHealth()}
                  className="px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                  busyText="Refreshing..."
                  busyClassName="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400"
                />
              }
            >
              <div className="relative space-y-4">
                {systemHealthCategoryKeys.length === 0 ? (
                  <div className="p-3 text-xs text-gray-500 dark:text-slate-400">
                    {systemHealthBusy
                      ? 'Loading system health…'
                      : 'No system health data found.'}
                  </div>
                ) : (
                  <>
                    {/* Summary strip: what needs attention, at a glance, without reading every table. */}
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60">
                      <div className="flex items-center gap-2 flex-wrap">
                        {systemHealthSummary.alarm > 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600 text-white text-xs font-semibold">
                            <CircleX className="h-3.5 w-3.5" aria-hidden />
                            {systemHealthSummary.alarm} in alarm
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold dark:bg-emerald-950/40 dark:text-emerald-200">
                            <CircleCheck className="h-3.5 w-3.5" aria-hidden />
                            All clear
                          </span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-slate-400">
                          {systemHealthSummary.ok} OK ·{' '}
                          {systemHealthSummary.noData} no data
                        </span>
                      </div>
                      <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-slate-300 select-none">
                        <input
                          type="checkbox"
                          checked={showOnlyProblems}
                          onChange={(e) =>
                            setShowOnlyProblems(e.target.checked)
                          }
                          className="rounded border-gray-300 dark:border-slate-600"
                        />
                        Show only alarms
                      </label>
                    </div>

                    {systemHealthCategoryKeys.map((category) => {
                      const allRows = systemHealth[category] ?? [];
                      const rows = showOnlyProblems
                        ? allRows.filter((r) =>
                            Object.values(r.metrics).some(
                              (m) => m.state === 'ALARM',
                            ),
                          )
                        : allRows;
                      const categoryAlarmCount =
                        systemHealthSummary.alarmsByCategory[category] ?? 0;
                      if (showOnlyProblems && rows.length === 0) return null;
                      const metricKeys = Array.from(
                        new Set(allRows.flatMap((r) => Object.keys(r.metrics))),
                      ).sort((a, b) => {
                        const order = [
                          'errors',
                          'throttles',
                          'duration',
                          'parse_error',
                          'action_failure',
                          'read_throttle',
                          'write_throttle',
                          'error_5xx',
                          'error_4xx',
                          'latency',
                        ];
                        const ai = order.indexOf(a);
                        const bi = order.indexOf(b);
                        if (ai !== -1 && bi !== -1) return ai - bi;
                        if (ai !== -1) return -1;
                        if (bi !== -1) return 1;
                        return a.localeCompare(b);
                      });

                      return (
                        <div
                          key={category}
                          className="rounded-lg border border-gray-200 p-3 bg-white dark:border-slate-700 dark:bg-slate-900"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                              {HEALTH_CATEGORY_LABELS[category] ?? category}
                            </div>
                            {categoryAlarmCount > 0 ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-semibold dark:bg-red-950/40 dark:text-red-200">
                                {categoryAlarmCount}
                              </span>
                            ) : null}
                          </div>
                          <div className="max-h-96 overflow-auto border border-gray-200 rounded dark:border-slate-700">
                            {rows.length === 0 ? (
                              <div className="p-3 text-xs text-gray-500 dark:text-slate-400">
                                No resources found.
                              </div>
                            ) : (
                              <table className="min-w-full text-xs">
                                <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800">
                                  <tr>
                                    <th className="text-left p-2 border-b border-gray-200 dark:border-slate-700">
                                      Resource
                                    </th>
                                    {metricKeys.map((metric) => (
                                      <th
                                        key={metric}
                                        className="text-left p-2 border-b border-gray-200 dark:border-slate-700"
                                      >
                                        {HEALTH_METRIC_LABELS[metric] ?? metric}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((row) => (
                                    <tr
                                      key={row.resource}
                                      className="odd:bg-white even:bg-gray-50/40 dark:even:bg-slate-800/40 dark:odd:bg-slate-900 dark:even:bg-slate-800/40"
                                    >
                                      <td className="p-2 border-b border-gray-100 dark:border-slate-700 font-mono text-[11px] text-gray-700 dark:text-slate-300 break-all">
                                        <div className="flex items-center gap-1.5">
                                          <span>{row.resource}</span>
                                          {category === 'lambda' ? (
                                            <button
                                              type="button"
                                              title="View recent logs"
                                              onClick={() =>
                                                setLambdaLogsFunction(
                                                  row.resource,
                                                )
                                              }
                                              className="text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200"
                                            >
                                              <FileText
                                                className="h-3.5 w-3.5"
                                                aria-hidden
                                              />
                                            </button>
                                          ) : null}
                                        </div>
                                      </td>
                                      {metricKeys.map((metric) => (
                                        <td
                                          key={metric}
                                          className="p-2 border-b border-gray-100 dark:border-slate-700"
                                        >
                                          <AlarmStatePill
                                            status={row.metrics[metric]}
                                          />
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                {systemHealthBusy ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-slate-900/80 backdrop-blur pointer-events-none">
                    <InlineSpinner size="sm" variant="accent" />
                  </div>
                ) : null}
              </div>
            </ExpandableSection>
          </div>

          {/* (session info moved to the single admin column above) */}
        </div>
      </div>
      <ConfirmDialog
        open={!!adminConfirm}
        danger
        busy={actionBusy}
        title={
          adminConfirm?.kind === 'deleteUser'
            ? 'Confirm delete user'
            : adminConfirm?.kind === 'deleteFirmware'
              ? 'Confirm firmware delete'
              : adminConfirm?.kind === 'removeOperator'
                ? 'Confirm operator removal'
                : 'Confirm'
        }
        message={
          adminConfirm?.kind === 'deleteUser' ? (
            <>
              Delete user{' '}
              <span className="font-semibold">
                {formatUserIdentityDisplay(
                  adminConfirm.userId,
                  userIdToDisplayName,
                )}
              </span>
              ?
            </>
          ) : adminConfirm?.kind === 'deleteFirmware' ? (
            <>
              Delete firmware{' '}
              <span className="font-semibold">{adminConfirm.role}</span>/{' '}
              <span className="font-semibold">{adminConfirm.fileName}</span>?
            </>
          ) : adminConfirm?.kind === 'removeOperator' ? (
            <>
              Remove operator{' '}
              <span className="font-semibold">
                {formatUserIdentityDisplay(
                  adminConfirm.userId,
                  userIdToDisplayName,
                )}
              </span>{' '}
              from event{' '}
              <span className="font-semibold">{adminConfirm.eventId}</span>?
            </>
          ) : null
        }
        confirmText={
          adminConfirm?.kind === 'deleteUser'
            ? 'Delete'
            : adminConfirm?.kind === 'deleteFirmware'
              ? 'Delete'
              : adminConfirm?.kind === 'removeOperator'
                ? 'Remove operator'
                : 'Confirm'
        }
        onConfirm={() => void performAdminConfirm()}
        onCancel={() => setAdminConfirm(null)}
      />
      {lambdaLogsFunction ? (
        <LambdaLogsDialog
          functionName={lambdaLogsFunction}
          onClose={() => setLambdaLogsFunction(null)}
        />
      ) : null}
    </div>
  );
}
